import { useRef, useState, DragEvent, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE_BYTES } from "src/config";
import { FormikDocument, InputUploadFileProps } from "src/types";
import { useBoolean } from "src/utils";

export const useInputUploadFile = ({
  documents,
  onDocumentsAdded,
  onDocumentRemoved,
  onUploadFile,
  onDeleteFile,
}: InputUploadFileProps) => {
  const { t } = useTranslation();
  const inputUploadFileRef = useRef<HTMLInputElement>(null);

  const [confirmDeleteAlert, showConfirmDeleteAlert, hideConfirmDeleteAlert] =
    useBoolean(false);
  const [
    errorUplaodingFiles,
    showErrorUplaodingFiles,
    hideErrorUplaodingFiles,
  ] = useBoolean(false);
  const [isOutOfSizeFile, setIsOutOfSizeFile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [documentToRemove, setDocumentToRemove] =
    useState<FormikDocument | null>(null);

  const validateFileSize = (file: File): boolean => {
    return file.size <= MAX_FILE_SIZE_BYTES;
  };

  const processFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;

    const newFiles = Array.from(fileList);
    const oversizedFiles = newFiles.filter((file) => !validateFileSize(file));

    if (oversizedFiles.length > 0) {
      setIsOutOfSizeFile(true);
      return;
    }

    setIsUploading(true);

    //Group api calls
    const promises = newFiles.map((file) => onUploadFile(file));

    const results = await Promise.allSettled(promises);
    const documentsToAdd: FormikDocument[] = [];

    for (const result of results) {
      if (result.status === "fulfilled") {
        const document = result.value;

        if (!document) continue;
        documentsToAdd.push(document);
      } else {
        if (!errorUplaodingFiles) showErrorUplaodingFiles();
      }
    }

    if (documentsToAdd.length > 0) onDocumentsAdded(documentsToAdd);

    setIsUploading(false);

    // Reset input
    if (inputUploadFileRef.current) {
      inputUploadFileRef.current.value = "";
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    processFiles(event.target.files);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    processFiles(event.dataTransfer.files);
  };

  const handleRemoveDocument = (document: FormikDocument) => {
    setDocumentToRemove(document);
    showConfirmDeleteAlert();
  };

  const confirmRemove = async () => {
    if (!documentToRemove?.documentId) {
      hideConfirmDeleteAlert();
      return;
    }

    try {
      const success = await onDeleteFile(documentToRemove.documentId);

      if (success) {
        onDocumentRemoved(documentToRemove.documentId);
      }
    } catch (error) {
      console.error("Failed to delete document:", error);
    }

    setDocumentToRemove(null);
    hideConfirmDeleteAlert();
  };

  return {
    t,
    documents,
    confirmDeleteAlert,
    isOutOfSizeFile,
    isUploading,
    inputUploadFileRef,
    ACCEPTED_FILE_TYPES,
    documentToRemove,
    errorUplaodingFiles,
    hideErrorUplaodingFiles,
    handleDrop,
    confirmRemove,
    hideConfirmDeleteAlert,
    handleFileChange,
    setIsOutOfSizeFile,
    handleRemoveDocument,
  };
};
